/**
 * Share-platform registry for the share sheets (company page, personal public
 * profile, CV).
 *
 * ONE list covers both things a platform can do with your link:
 *   - `stepsKey`     — a walkthrough for putting the link in your profile bio,
 *                      permanently. Borrowed in shape from link-in-bio tools:
 *                      short numbered steps naming the exact field, rather than
 *                      one paragraph of prose.
 *   - `shareIntent`  — a one-off post composed on that platform.
 *
 * They used to be two separate sections in the sheet, which showed the same
 * logos twice: five of the six "Share to" tiles duplicated something already in
 * the bio row. Now a platform appears once and its detail view offers whichever
 * of the two actions it actually supports.
 *
 * Order is by relevance to a hiring page (LinkedIn first, not TikTok). Copy
 * lives in i18n (`common:linkInBio.*`).
 */
import type { ProfileIconId } from '../components/profile/ProfileIconRegistry';

export type LinkInBioPlatformId =
  | 'linkedin'
  | 'linkedin_page'
  | 'instagram'
  | 'x'
  | 'facebook'
  | 'facebook_page'
  | 'youtube'
  | 'tiktok'
  | 'whatsapp'
  | 'telegram'
  | 'snapchat'
  | 'google_business';

/**
 * Which share sheets a platform belongs on. A company page has no personal
 * Instagram bio to edit, and an individual has no Google Business Profile — so
 * each sheet renders only what its owner can actually act on.
 */
export type LinkInBioAudience = 'personal' | 'company' | 'both';

export interface LinkInBioPlatform {
  id: LinkInBioPlatformId;
  /**
   * Brand glyph from ProfileIconRegistry. Omitted where we have no faithful
   * brand mark — `symbol` is drawn instead rather than borrowing another
   * company's logo.
   */
  icon?: ProfileIconId;
  /** Material Symbol name used when `icon` is absent. */
  symbol?: string;
  /** `common:linkInBio.platforms.<id>` */
  labelKey: string;
  /**
   * `common:linkInBio.steps.<key>` — an ARRAY of step strings, read with
   * `returnObjects: true`. A Facebook Page and a Facebook profile do not share
   * a walkthrough, so this is explicit rather than derived from `id`.
   *
   * Absent where the platform has no usable bio link field (Telegram's bio has
   * no website field, is 70 characters on a free account, and does not render
   * links as tappable). Those platforms are share-only: their tile composes a
   * post directly instead of opening a walkthrough.
   */
  stepsKey?: string;
  /**
   * Builds the platform's web share-composer URL from an encoded page URL.
   * Absent where the platform publishes no share intent — LinkedIn and Snapchat
   * are deliberately omitted rather than guessed at.
   */
  shareIntent?: (encodedUrl: string) => string;
  /** Optional one-liner above the steps, e.g. "You'll need your phone." */
  prereqKey?: string;
  /** Optional eligibility warning rendered as a callout card. */
  noticeKey?: string;
  /** Optional caveat rendered under the steps. */
  footnoteKey?: string;
  /**
   * Desktop web destination — as close to the edit screen as the platform
   * allows without knowing the user's handle. Some platforms only expose the
   * bio field in their mobile app; those point at the account/settings page and
   * their steps say so.
   */
  webUrl: string;
  /**
   * Mobile app deep link. Native tries this first and falls back to `webUrl`.
   *
   * Only set where the app beats the web destination, because a scheme is a
   * blunt instrument: it opens the app's home, while an https `webUrl` on a
   * domain the app claims (linkedin.com, x.com, facebook.com) opens the SAME
   * app *and* lands on the exact field the steps describe. So this is reserved
   * for the platforms whose field the web simply cannot reach.
   *
   * Every scheme here must also be declared in `ios.infoPlist
   * .LSApplicationQueriesSchemes` (app.json) and in the Android `<queries>`
   * intents (plugins/withSharePlatformAndroidQueries.js) — otherwise
   * `Linking.canOpenURL` answers false however the app is installed, and iOS
   * logs a "not allowed to query for scheme" warning on every tap.
   */
  appUrl?: string;
  /** True when the bio/website field is only editable in the mobile app. */
  appOnly?: boolean;
  audience: LinkInBioAudience;
}

/**
 * Display order, most useful first for a professional page. `getLinkInBioPlatforms`
 * filters this by audience without reordering.
 */
export const LINK_IN_BIO_PLATFORMS: LinkInBioPlatform[] = [
  {
    id: 'linkedin_page',
    icon: 'linkedin',
    labelKey: 'linkInBio.platforms.linkedin_page',
    stepsKey: 'linkInBio.steps.linkedin_page',
    noticeKey: 'linkInBio.notices.linkedin_page',
    webUrl: 'https://www.linkedin.com/',
    audience: 'company',
  },
  {
    id: 'linkedin',
    icon: 'linkedin',
    labelKey: 'linkInBio.platforms.linkedin',
    stepsKey: 'linkInBio.steps.linkedin',
    footnoteKey: 'linkInBio.footnotes.linkedin',
    // No `appUrl`: linkedin.com is a universal link, so this opens the app when
    // it is installed and lands on the profile whose Contact info the steps
    // edit — `linkedin://profile` would only open the app's feed.
    webUrl: 'https://www.linkedin.com/in/me/',
    audience: 'personal',
  },
  {
    id: 'instagram',
    icon: 'instagram',
    labelKey: 'linkInBio.platforms.instagram',
    stepsKey: 'linkInBio.steps.instagram',
    footnoteKey: 'linkInBio.footnotes.instagram',
    // NOT `appOnly`: this is the desktop Edit profile page, which carries the
    // same Links field the app does. The steps are worded for both.
    webUrl: 'https://www.instagram.com/accounts/edit/',
    appUrl: 'instagram://app',
    audience: 'both',
  },
  {
    id: 'x',
    icon: 'x',
    labelKey: 'linkInBio.platforms.x',
    stepsKey: 'linkInBio.steps.x',
    shareIntent: (url) => `https://twitter.com/intent/tweet?url=${url}`,
    // No `appUrl`: x.com is a universal link and this path lands directly on
    // the Website field; `twitter://` would only open the timeline.
    webUrl: 'https://x.com/settings/profile',
    audience: 'both',
  },
  {
    id: 'facebook_page',
    icon: 'facebook',
    labelKey: 'linkInBio.platforms.facebook_page',
    stepsKey: 'linkInBio.steps.facebook_page',
    footnoteKey: 'linkInBio.footnotes.facebook_page',
    shareIntent: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    webUrl: 'https://www.facebook.com/pages/?category=your_pages',
    audience: 'company',
  },
  {
    id: 'facebook',
    icon: 'facebook',
    labelKey: 'linkInBio.platforms.facebook',
    stepsKey: 'linkInBio.steps.facebook',
    footnoteKey: 'linkInBio.footnotes.facebook',
    shareIntent: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    // No `appUrl`: facebook.com is a universal link and this path lands on the
    // contact-info editor itself; `fb://profile` would only open your profile.
    webUrl: 'https://www.facebook.com/me/about_contact_and_basic_info',
    audience: 'personal',
  },
  {
    id: 'youtube',
    icon: 'youtube',
    labelKey: 'linkInBio.platforms.youtube',
    stepsKey: 'linkInBio.steps.youtube',
    noticeKey: 'linkInBio.notices.youtube',
    footnoteKey: 'linkInBio.footnotes.youtube',
    webUrl: 'https://www.youtube.com/customize',
    audience: 'both',
  },
  {
    id: 'tiktok',
    icon: 'tiktok',
    labelKey: 'linkInBio.platforms.tiktok',
    stepsKey: 'linkInBio.steps.tiktok',
    prereqKey: 'linkInBio.prereq.app',
    noticeKey: 'linkInBio.notices.tiktok',
    webUrl: 'https://www.tiktok.com/setting',
    appUrl: 'snssdk1233://',
    appOnly: true,
    audience: 'both',
  },
  {
    id: 'whatsapp',
    icon: 'whatsapp',
    labelKey: 'linkInBio.platforms.whatsapp',
    stepsKey: 'linkInBio.steps.whatsapp',
    prereqKey: 'linkInBio.prereq.phone',
    noticeKey: 'linkInBio.notices.whatsapp',
    shareIntent: (url) => `https://wa.me/?text=${url}`,
    webUrl: 'https://web.whatsapp.com/',
    appUrl: 'whatsapp://',
    appOnly: true,
    audience: 'both',
  },
  {
    id: 'google_business',
    symbol: 'storefront',
    labelKey: 'linkInBio.platforms.google_business',
    stepsKey: 'linkInBio.steps.google_business',
    noticeKey: 'linkInBio.notices.google_business',
    webUrl: 'https://business.google.com/',
    audience: 'company',
  },
  {
    id: 'telegram',
    icon: 'telegram',
    labelKey: 'linkInBio.platforms.telegram',
    shareIntent: (url) => `https://t.me/share/url?url=${url}`,
    // No `appUrl`: with no `stepsKey` this platform never opens the guide, so
    // nothing would ever follow the deep link — its tile composes a post.
    webUrl: 'https://web.telegram.org/',
    audience: 'both',
  },
  {
    id: 'snapchat',
    icon: 'snapchat',
    labelKey: 'linkInBio.platforms.snapchat',
    stepsKey: 'linkInBio.steps.snapchat',
    noticeKey: 'linkInBio.notices.snapchat',
    webUrl: 'https://accounts.snapchat.com/',
    appUrl: 'snapchat://',
    appOnly: true,
    audience: 'both',
  },
];

const BY_ID = new Map(LINK_IN_BIO_PLATFORMS.map((p) => [p.id, p]));

export function getLinkInBioPlatform(
  id: LinkInBioPlatformId,
): LinkInBioPlatform | undefined {
  return BY_ID.get(id);
}

export function isLinkInBioPlatformId(
  value: string,
): value is LinkInBioPlatformId {
  return BY_ID.has(value as LinkInBioPlatformId);
}

/**
 * The platforms a given sheet shows, in display order. `company` mode swaps the
 * personal LinkedIn/Facebook entries for their Page equivalents and adds Google
 * Business Profile; `personal` does the reverse.
 */
export function getLinkInBioPlatforms(
  audience: Exclude<LinkInBioAudience, 'both'>,
): LinkInBioPlatform[] {
  return LINK_IN_BIO_PLATFORMS.filter(
    (p) => p.audience === 'both' || p.audience === audience,
  );
}

/**
 * Reads a `stepsKey` through i18next. Locale files store the walkthrough as a
 * string array; anything else (a missing key returns the key itself) yields an
 * empty list rather than rendering `linkInBio.steps.x` as a step.
 */
export function readSteps(
  translate: (key: string, options: { returnObjects: true }) => unknown,
  stepsKey: string | undefined,
): string[] {
  if (!stepsKey) return [];
  const value = translate(stepsKey, { returnObjects: true });
  if (!Array.isArray(value)) return [];
  return value.filter((step): step is string => typeof step === 'string');
}
